<template lang="pug">
    v-hover(v-if="validConversationGroup")
        template(v-slot:default='{ hover }')
            v-card.mx-lg-auto(
                outlined
                width="100%"
                rounded
                :color="cardColor"
                :dark="isSelected"
            )
                v-toolbar(
                    flat
                    rounded
                    :color="cardColor"
                )
                    v-icon(
                        color="error"
                        small
                        :style="{ visibility: displayBeacon ? 'visible' : 'hidden' }"
                    ).mr-sm-4
                        | mdi-circle
                    v-icon.mr-sm-4(large)
                        template(v-if="conversationGroup.total_participants > 2") mdi-account-group
                        template(v-else) mdi-account
                    v-toolbar-title
                        | {{ conversationGroup.customer_full_name }}
                        br
                        .text-body-2
                            | {{ conversationGroup.organization_name }}
                    v-spacer
                    .text-right(style="width: 300px;")
                        | {{ displayMessageTime }}
                        br
                        | {{ conversationGroup.customer_conversation_group_person_phone_number | tel }}

                v-card-text.pt-0.pb-sm-2(style="padding-left: 100px; padding-right: 100px;")
                    v-row.ma-0.pa-0
                        v-col.ma-0.pa-0(v-if="awaitingReply" cols="1")
                            v-icon(small) mdi-arrow-right-bottom
                        v-col.ma-0.pa-0
                            .message-body-contain {{ displayMessageBody }}

                v-fade-transition
                    v-overlay(v-if='hover' absolute color='var(--volie-gray)')
                        v-row.ma-0.d-flex.justify-center.align-center(style="row-gap: 10px;")
                            v-btn.mark-as-read(
                                v-if="displayBeacon"
                                color="success" 
                                dark 
                                rounded
                                :loading="isUpdateCommunicationResponsesStatusLoading"
                                @click="updateCommunicationResponseStatusForMessageUnresponded(lastMessageID)"
                            )
                                v-tooltip(top)
                                    template(v-slot:activator='{ on, attrs }')
                                        span(v-bind="attrs" v-on="on" role="text")
                                            v-icon(color="white" small) mdi-check
                                    | Acknowledge Message
                            v-btn.mx-3(v-if="customerID > 0" :color="colorCode(viewingCustomerProfile)" :dark="!viewingCustomerProfile" rounded ripple dense @click="handleClick('customer_profile');")
                                v-tooltip(top)
                                    template(v-slot:activator='{ on, attrs }')
                                        span(v-bind="attrs" v-on="on" role="text")
                                            v-icon(:color="colorCode(!viewingCustomerProfile)") mdi-account
                                    span
                                        | Open Profile

                            v-btn.mx-3(v-if="validEmailClient" :color="colorCode(viewingEmailClient)" :dark="!viewingEmailClient" rounded ripple dense @click="handleClick('email_client');")
                                v-tooltip(top)
                                    template(v-slot:activator='{ on, attrs }')
                                        span(v-bind="attrs" v-on="on" role="text")
                                            v-icon(:color="colorCode(!viewingEmailClient)") mdi-email
                                    span
                                        | Open Inbox

                            v-btn.mx-3(v-if="validMessageSoftphone" :color="colorCode(viewingMessageSoftphone)" :dark="!viewingMessageSoftphone" rounded ripple dense @click="handleClick('message_softphone');")
                                v-tooltip(top)
                                    template(v-slot:activator='{ on, attrs }')
                                        span(v-bind="attrs" v-on="on" role="text")
                                            v-icon(:color="colorCode(!viewingMessageSoftphone)") mdi-cellphone
                                    span
                                        | Open Softphone

                            v-btn.mx-3(v-if="renderManualModeCall" color="warning" dark rounded ripple dense @click="enterManualMode(conversationGroup.last_campaign, 'sms_message', false, forceCustomer);")
                                v-tooltip(top)
                                    template(v-slot:activator='{ on, attrs }')
                                        span(v-bind="attrs" v-on="on" role="text")
                                            v-icon mdi-pencil
                                    span
                                        | Disposition Message

                            v-btn.mx-3(v-if="renderManualModeCall" color="warning" dark rounded ripple dense @click="enterManualMode(conversationGroup.last_campaign, 'phone', false, forceCustomer);")
                                v-tooltip(top)
                                    template(v-slot:activator='{ on, attrs }')
                                        span(v-bind="attrs" v-on="on" role="text")
                                            v-icon mdi-phone-outgoing
                                    span
                                        | Phone Call

</template>

<script lang="ts">
import Vue from 'vue';
import VolieState  from "@/store";
import { UpdateCommunicationResponseStatusForMessageUnresponded } from '@/data/communication_response';

export default Vue.extend({
    data() {
        return {
            hasAcknowledged: false,
            isUpdateCommunicationResponsesStatusLoading: false,
        }
    },
    methods: {
        colorCode(result) {
            return result === true ? "var(--volie-teal)" : "var(--white)";
        },
        handleClick(comp) {
            this.selectConversationGroup(this.conversationGroup, comp);
        },
        updateCommunicationResponseStatusForMessageUnresponded(messageID: number) {
            const options = {
                eventable_id: this.currentUser?.user_id || 0,
                eventable_type: "user",
                communication_response_status: "acknowledged_no_response",
                message_id: messageID,
            }

            this.isUpdateCommunicationResponsesStatusLoading = true;

            UpdateCommunicationResponseStatusForMessageUnresponded(options).then((response) => {
                if (!response.Error) {
                    this.hasAcknowledged = !this.hasAcknowledged;
                    this.showSnackbar('var(--success)', 'Message status updated');
                } else {
                    this.showSnackbar('var(--error)', 'Unable to change message status');
                }
            }).finally(() => {
                this.isUpdateCommunicationResponsesStatusLoading = false;
            });
        },
    },
    computed: {
        accountID() {
            return (this.conversationGroup?.account_id || 0);
        },
        awaitingReply() {
            return this.conversationGroup?.has_unresponded_incoming_message === true;
        },
        campaignID() {
            return (this.conversationGroup?.last_campaign?.campaign_id || 0);
        },
        cardColor() {
            return this.isSelected ? "primary" : null;
        },
        conversationGroupID() {
            return (this.conversationGroup?.conversation_group_id || 0);
        },
        currentUser() {
            return VolieState.currentUser();
        },
        customerID() {
            return (this.conversationGroup?.customer_id || 0);
        },
        customerPhoneNumberID() {
            return (this.conversationGroup?.customer_phone_number_id || 0);
        },
        displayMessageBody() {
            return this.conversationGroup?.filtered_message_body || this.conversationGroup?.last_message_body;
        },
        displayMessageTime() {
            return this.conversationGroup?.friendly_filtered_message_time || this.conversationGroup?.friendly_last_message_time;
        },
        displayBeacon() {
            if (this.hasAcknowledged || this.conversationGroup?.communication_response_status === "dispositioned_no_response") {
                return false;
            } else {
                return this.hasUnreadMessages || this.awaitingReply;
            }
        },
        forceCustomer() {
            return {
                campaign_customer_id: null,
                customer_id: this.customerID,
                next_attemptable_id: this.customerPhoneNumberID,
                next_attemptable_type: "customer_phone_number",
                next_attemptable_value: null,
                noticeable_id: this.lastMessageID,
                noticeable_type: "message",
                vehicle_id: null,
            }
        },
        hasUnreadMessages() {
            return this.conversationGroup?.has_unread_messages === true;
        },
        isSelected() {
            return this.selectedConversationGroupId === this.conversationGroupID;
        },
        lastMessageID() {
            return (this.conversationGroup?.last_message_id || 0);
        },
        renderManualModeCall() {
            return this.validEnterManualMode && this.validCampaignID;
        },
        twilioNumberID() {
            return (this.conversationGroup?.twilio_number_id || 0);
        },
        validCampaignID() {
            return this.campaignID > 0;
        },
        validConversationGroup() {
            return this.conversationGroupID > 0;
        },
        validEmailClient() {
            return this.accountID > 0 && this.customerID > 0;
        },
        validEnterManualMode() {
            return this.enterManualMode !== undefined && this.enterManualMode !== null;
        },
        validMessageSoftphone() {
            return this.accountID > 0 && this.customerPhoneNumberID > 0 && this.twilioNumberID;
        },
        viewingCustomerProfile() {
            return this.isSelected && this.selectedComponents?.length > 0 && this.selectedComponents.indexOf("customer_profile") >= 0;
        },
        viewingEmailClient() {
            return this.isSelected && this.selectedComponents?.length > 0 && this.selectedComponents.indexOf("email_client") >= 0;
        },
        viewingMessageSoftphone() {
            return this.isSelected && this.selectedComponents?.length > 0 && this.selectedComponents.indexOf("message_softphone") >= 0;
        },
    },
    components: {
    },
    props: {
        conversationGroup: Object,
        enterManualMode: Function,
        selectedComponents: Array,
        selectConversationGroup: Function,
        selectedConversationGroupId: Number,
        showSnackbar: Function,
    },
    created() {
        this.hasAcknowledged = this.conversationGroup?.communication_response_status === "acknowledged_no_response";
    },
    destroyed() {
    },
    mounted() {
    },
})
</script>

<style lang="scss" scoped>
    #app {
        .message-card-divider {
            margin: 0 40px 0 40px;
        }

        .message-body-contain {
             display: -webkit-box;
             max-width: 100%;
             margin: 0 auto;
             -webkit-line-clamp: 2;
             -webkit-box-orient: vertical;
             overflow: hidden;
             text-overflow: ellipsis;
             height: 44px;
        }

        .mark-as-read {
            min-width: auto;
            width: auto;
            height: auto;
            padding: 5px;
            transition: color 0.3s ease;
        }
    }
</style>